import { SubscriptionPlanType, SubscriptionStatus } from 'models/user/userWorkspaceDTOs'

import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { FC } from 'react'

export enum PlanCardVariant {
  SUBSCRIBE = 'subscribe',
  MANAGE = 'manage',
}

export const PLAN_ORDER = [
  SubscriptionPlanType.PERSONAL,
  SubscriptionPlanType.PRO,
  SubscriptionPlanType.ENTERPRISE,
]

export const PLAN_RANK: Record<SubscriptionPlanType, number> = {
  [SubscriptionPlanType.PERSONAL]: 0,
  [SubscriptionPlanType.PRO]: 1,
  [SubscriptionPlanType.ENTERPRISE]: 2,
}

/**
 * Plans sold per workspace and bought without sales, so a second subscription means a second
 * workspace. Enterprise is contracted, not self-served, and is deliberately absent.
 */
export const SELF_SERVE_SUBSCRIPTION_PLAN_TYPES: SubscriptionPlanType[] = [
  SubscriptionPlanType.PRO,
]

/**
 * Statuses in which a subscription needs nothing from its owner, so buying a second one is the
 * sensible next step. A payment that is failing or a term that is running out has to be settled
 * first — renewing is the action there, not buying again.
 */
export const SETTLED_SUBSCRIPTION_STATUSES: SubscriptionStatus[] = [
  SubscriptionStatus.NEW,
  SubscriptionStatus.ACTIVE,
]

/** Sub-key each plan's copy lives under in the `pricing_page:plans` catalog */
export const PLAN_COPY_KEY: Record<SubscriptionPlanType, string> = {
  [SubscriptionPlanType.PERSONAL]: 'starter',
  [SubscriptionPlanType.PRO]: 'pro',
  [SubscriptionPlanType.ENTERPRISE]: 'enterprise',
}

export const PLAN_ICONS: Record<SubscriptionPlanType, FC> = {
  [SubscriptionPlanType.PERSONAL]: PersonOutlineIcon,
  [SubscriptionPlanType.PRO]: BusinessOutlinedIcon,
  [SubscriptionPlanType.ENTERPRISE]: BusinessOutlinedIcon,
}

/**
 * Key of the "everything in the plan below" line, or undefined for the entry plan, which has
 * nothing below it. Both the plan cards and the workspace plan banner render this line, so the
 * rule lives here rather than in each surface.
 */
export const getEverythingPrefixKey = (planType: SubscriptionPlanType): string | undefined => (
  planType === SubscriptionPlanType.PERSONAL
    ? undefined
    : `pricing_page:plans.${PLAN_COPY_KEY[planType]}.everything_prefix`
)

/** Feature lines per plan, in display order. The plan cards and the workspace plan banner render the same list. */
export const PLAN_FEATURES: Record<SubscriptionPlanType, string[]> = {
  [SubscriptionPlanType.PERSONAL]: [
    'pricing_page:plans.starter.features.delivery_2bd',
    'pricing_page:plans.starter.features.order_instantly',
    'pricing_page:plans.starter.features.upload_own',
    'pricing_page:plans.starter.features.mobile_app',
  ],
  [SubscriptionPlanType.PRO]: [
    'pricing_page:plans.pro.features.delivery_1bd',
    'pricing_page:plans.pro.features.choose_photographer',
    'pricing_page:plans.pro.features.photo_floorplan_templates',
    'pricing_page:plans.pro.features.photo_editing_tool',
    'pricing_page:plans.pro.features.ai_editing',
    'pricing_page:plans.pro.features.ai_text_generator',
    'pricing_page:plans.pro.features.team_access_collab',
    'pricing_page:plans.pro.features.admin_user_management',
    'pricing_page:plans.pro.features.bulk_export',
    'pricing_page:plans.pro.features.public_api',
    'pricing_page:plans.pro.features.payment_invoice_email',
  ],
  [SubscriptionPlanType.ENTERPRISE]: [
    'pricing_page:plans.enterprise.features.custom_packs',
    'pricing_page:plans.enterprise.features.user_access_settings',
    'pricing_page:plans.enterprise.features.monthly_invoice_orders',
  ],
}
